import React, { useState, useMemo } from 'react';
import { CompleteKundali, KundaliChartData, Language, PlanetPosition, PlanetName } from '../types/kundali';
import { getPlanetShortCode, getZodiacName, getPlanetFullName } from '../utils/language';
import {
  calculatePratyantardashas,
  getMultiYearDashaHorizon,
  formatDashaMonthYear,
  formatDashaDayMonth,
  formatDashaDateYear,
  formatDashaFullDate,
  PratyantarDashaItem,
  DashaHorizonItem
} from '../utils/dashaCalculator';
import {
  calculateGocharTransits,
  calculateLivePlanetaryEphemeris,
  ZODIAC_SIGNS,
  ZODIAC_LORDS
} from '../utils/vedicEngine';
import {
  Clock,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Sparkles,
  AlertTriangle,
  Info,
  Copy,
  Check,
  Calendar,
  Layers,
  ArrowRight,
  Shield,
  ShieldAlert,
  Compass,
  Eye,
  Activity,
  Zap,
  Globe,
  Radio
} from 'lucide-react';

export interface AspectedHouseInfo {
  houseNumber: number;
  occupyingPlanets: string[];
}

export function calculateVedicAspects(
  planetName: string,
  placedHouse: number | null,
  chartPlanets?: PlanetPosition[]
): AspectedHouseInfo[] {
  if (!placedHouse || placedHouse < 1 || placedHouse > 12) return [];

  const targetHouse = (offset: number) => ((placedHouse - 1 + offset) % 12) + 1;
  const aspectHouses: number[] = [targetHouse(6)]; // 7th aspect for all planets

  if (planetName === 'Mars') {
    aspectHouses.push(targetHouse(3)); // 4th aspect
    aspectHouses.push(targetHouse(7)); // 8th aspect
  } else if (planetName === 'Jupiter') {
    aspectHouses.push(targetHouse(4)); // 5th aspect
    aspectHouses.push(targetHouse(8)); // 9th aspect
  } else if (planetName === 'Saturn') {
    aspectHouses.push(targetHouse(2)); // 3rd aspect
    aspectHouses.push(targetHouse(9)); // 10th aspect
  } else if (planetName === 'Rahu' || planetName === 'Ketu') {
    aspectHouses.push(targetHouse(4)); // 5th aspect
    aspectHouses.push(targetHouse(8)); // 9th aspect
  }

  const uniqueHouses = Array.from(new Set(aspectHouses)).sort((a, b) => a - b);

  return uniqueHouses.map((hNum) => {
    const occ = (chartPlanets || [])
      .filter((p) => p.house === hNum && p.name !== 'Ascendant')
      .map((p) => p.name);
    return {
      houseNumber: hNum,
      occupyingPlanets: occ
    };
  });
}

interface UnifiedDashaPanelProps {
  kundali: CompleteKundali;
  chartData: KundaliChartData;
  activeLang: Language;
  isHi: boolean;
  selectedPlanet: PlanetPosition | null;
  onSelectPlanet: (planet: PlanetPosition | null) => void;
  onSelectHouse: (houseNumber: number) => void;
  onOpenDashaDrawer: () => void;
  isNarrow?: boolean;
  shaniStatus?: {
    isVisible: boolean;
    badge?: string;
    phase?: string;
    saturnSign?: string;
    endDate?: string;
    alertType?: 'warning' | 'info' | 'neutral' | string;
    text?: string;
  };
  dashaMarakaAlert?: {
    isAlert?: boolean;
    header: string;
    level: 'warning' | 'info' | 'neutral' | 'nominal' | string;
    text: string;
  };
  marakaDiagnosis?: {
    lord2: string;
    lord7: string;
    marakaPlanets: string[];
    badhakesh: string;
  };
}

export const UnifiedDashaPanel: React.FC<UnifiedDashaPanelProps> = ({
  kundali,
  chartData,
  activeLang,
  isHi,
  selectedPlanet,
  onSelectPlanet,
  onSelectHouse,
  onOpenDashaDrawer,
  isNarrow = false,
  shaniStatus,
  dashaMarakaAlert,
  marakaDiagnosis
}) => {
  // View toggle: 'pds' (current 9 micro-phases) or 'horizon' (next 2-5 years) or null (collapsed by default)
  const [activeTab, setActiveTab] = useState<'pds' | 'horizon' | null>(null);
  // Expanded horizon items for inline accordion
  const [expandedHorizonId, setExpandedHorizonId] = useState<string | null>(null);
  // Copy to clipboard state
  const [copiedTriad, setCopiedTriad] = useState(false);

  const ascSignIdx = chartData.ascendantIndex ?? 0;
  const isDivisional = chartData.divisionalCode && chartData.divisionalCode !== 'D1';

  // Helper: Map planetary lordship & placement for active chart
  const getPlanetChartMeta = (planetName: PlanetName | string) => {
    const pos = chartData.planets?.find(p => p.name === planetName);
    const d1Pos = kundali.planets?.find(p => p.name === planetName);
    const placedHouse = pos?.house ?? null;
    const d1PlacedHouse = d1Pos?.house ?? null;
    const effectivePlacedHouse = placedHouse ?? d1PlacedHouse;

    const ZODIAC_LORDS = ['Mars', 'Venus', 'Mercury', 'Moon', 'Sun', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Saturn', 'Jupiter'];
    
    // Active chart ruled houses
    const ruledHouses: number[] = [];
    for (let h = 1; h <= 12; h++) {
      const sIdx = (ascSignIdx + h - 1) % 12;
      if (ZODIAC_LORDS[sIdx] === planetName) {
        ruledHouses.push(h);
      }
    }

    // D1 ruled houses for reference
    const d1AscIdx = kundali.charts?.D1?.ascendantIndex ?? 0;
    const d1RuledHouses: number[] = [];
    for (let h = 1; h <= 12; h++) {
      const sIdx = (d1AscIdx + h - 1) % 12;
      if (ZODIAC_LORDS[sIdx] === planetName) {
        d1RuledHouses.push(h);
      }
    }

    // Chara Karaka
    const charaKaraka = kundali.charaKarakas?.find(k => k.planet === planetName);

    // Calculate Vedic Drishti (Aspects)
    const aspects = calculateVedicAspects(
      planetName,
      effectivePlacedHouse,
      chartData.planets || kundali.planets
    );

    return {
      pos,
      d1Pos,
      placedHouse,
      d1PlacedHouse,
      effectivePlacedHouse,
      ruledHouses,
      d1RuledHouses,
      aspects,
      isRetrograde: pos?.isRetrograde ?? d1Pos?.isRetrograde ?? false,
      isCombust: pos?.isCombust ?? d1Pos?.isCombust ?? false,
      dignity: pos?.dignity ?? d1Pos?.dignity,
      charaKarakaCode: charaKaraka?.code
    };
  };

  // 1. Resolve Active Triad (MD -> AD -> PD)
  const activeTriad = useMemo(() => {
    const nowMs = Date.now();

    // 1. Active Mahadasha
    let md = kundali.dashas?.find(d => {
      const s = new Date(d.startDate).getTime();
      const e = new Date(d.endDate).getTime();
      return nowMs >= s && nowMs <= e;
    });
    if (!md) md = kundali.dashas?.find(d => d.isCurrent) || kundali.dashas?.[0];

    // 2. Active Antardasha
    const adList = md?.antardashas || [];
    let ad = adList.find(a => {
      const s = new Date(a.startDate).getTime();
      const e = new Date(a.endDate).getTime();
      return nowMs >= s && nowMs <= e;
    });
    if (!ad) ad = adList.find(a => a.isCurrent) || adList[0];

    // 3. Active Pratyantardasha (Dynamic zero-drift calculation)
    let pds: PratyantarDashaItem[] = [];
    let pd: PratyantarDashaItem | undefined;

    if (ad) {
      pds = calculatePratyantardashas(ad.planet, ad.startDate, ad.endDate);
      pd = pds.find(p => p.isCurrent) || pds[0];
    }

    // Days remaining & progress percent on AD
    let adDaysRemaining = 0;
    let adProgressPercent = 50;
    if (ad?.startDate && ad?.endDate) {
      const s = new Date(ad.startDate).getTime();
      const e = new Date(ad.endDate).getTime();
      if (e > s) {
        adProgressPercent = Math.min(100, Math.max(0, Math.round(((nowMs - s) / (e - s)) * 100)));
        adDaysRemaining = Math.max(0, Math.ceil((e - nowMs) / (1000 * 60 * 60 * 24)));
      }
    }

    return {
      md,
      ad,
      pd,
      pds,
      adDaysRemaining,
      adProgressPercent,
      mdMeta: md ? getPlanetChartMeta(md.planet) : null,
      adMeta: ad ? getPlanetChartMeta(ad.planet) : null,
      pdMeta: pd ? getPlanetChartMeta(pd.planet) : null
    };
  }, [kundali.dashas, chartData, ascSignIdx]);

  // 2. Resolve Multi-Year Horizon (Seamless across MD boundaries)
  const horizonPeriods = useMemo(() => {
    return getMultiYearDashaHorizon(kundali.dashas || [], kundali.birthDetails?.date, {
      maxPeriods: 6
    });
  }, [kundali.dashas, kundali.birthDetails?.date]);

  // 3. Resolve Live Dynamic Gochar (Transits) Relative to Native Lagna & Moon
  const gocharSnapshot = useMemo(() => {
    const transits = calculateGocharTransits(kundali.planets);
    const saturnTransit = transits.items.find(i => i.planet === 'Saturn');
    const jupiterTransit = transits.items.find(i => i.planet === 'Jupiter');
    const marsTransit = transits.items.find(i => i.planet === 'Mars');
    const rahuTransit = transits.items.find(i => i.planet === 'Rahu');
    const ketuTransit = transits.items.find(i => i.planet === 'Ketu');

    // Double transit check (Saturn + Jupiter concurrent influence)
    const saturnAspects = calculateVedicAspects('Saturn', saturnTransit?.houseFromLagna ?? null);
    const jupiterAspects = calculateVedicAspects('Jupiter', jupiterTransit?.houseFromLagna ?? null);

    const saturnHouses = [
      saturnTransit?.houseFromLagna,
      ...saturnAspects.map(a => a.houseNumber)
    ].filter(Boolean) as number[];

    const jupiterHouses = [
      jupiterTransit?.houseFromLagna,
      ...jupiterAspects.map(a => a.houseNumber)
    ].filter(Boolean) as number[];

    const doubleTransitHouses = Array.from(
      new Set(saturnHouses.filter(h => jupiterHouses.includes(h)))
    ).sort((a, b) => a - b);

    // Confluence with active Dasha houses
    const activeDashaHouses = [
      ...(activeTriad.mdMeta?.ruledHouses || []),
      activeTriad.mdMeta?.effectivePlacedHouse,
      ...(activeTriad.adMeta?.ruledHouses || []),
      activeTriad.adMeta?.effectivePlacedHouse,
      ...(activeTriad.pdMeta?.ruledHouses || []),
      activeTriad.pdMeta?.effectivePlacedHouse
    ].filter(Boolean) as number[];

    const confluenceHouses = doubleTransitHouses.filter(h => activeDashaHouses.includes(h));

    return {
      transits,
      saturnTransit,
      jupiterTransit,
      marsTransit,
      rahuTransit,
      ketuTransit,
      doubleTransitHouses,
      confluenceHouses,
      saturnAspects,
      jupiterAspects
    };
  }, [kundali.planets, activeTriad]);

  // Handler: Select a planet and its house in the parent cockpit
  const handleSpotlightPlanet = (planetName: PlanetName | string) => {
    const pPos = chartData.planets?.find(p => p.name === planetName) || kundali.planets?.find(p => p.name === planetName);
    if (pPos) {
      onSelectPlanet(pPos);
      if (pPos.house) {
        onSelectHouse(pPos.house);
      }
    }
  };

  // Handler: Copy Triad to Clipboard
  const handleCopyTriad = () => {
    if (!activeTriad.md || !activeTriad.ad || !activeTriad.pd) return;
    const text = `Kundali Dasha Triad (${kundali.birthDetails?.name || 'Native'}):
MD: ${activeTriad.md.planet} (${formatDashaDateYear(activeTriad.md.startDate)}–${formatDashaDateYear(activeTriad.md.endDate)})
AD: ${activeTriad.ad.planet} (${formatDashaMonthYear(activeTriad.ad.startDate)}–${formatDashaMonthYear(activeTriad.ad.endDate)}) [${activeTriad.adDaysRemaining}d remaining]
PD: ${activeTriad.pd.planet} (${formatDashaDayMonth(activeTriad.pd.startDate)}–${formatDashaDayMonth(activeTriad.pd.endDate)}) [${activeTriad.pd.daysRemaining}d remaining]
Active Rulership: MD(H${activeTriad.mdMeta?.ruledHouses.join('/')}) → AD(H${activeTriad.adMeta?.ruledHouses.join('/')}) → PD(H${activeTriad.pdMeta?.ruledHouses.join('/')})`;

    navigator.clipboard.writeText(text);
    setCopiedTriad(true);
    setTimeout(() => setCopiedTriad(false), 2200);
  };

  return (
    <div className="w-full bg-[#FDFCF9] border border-[#E2DED5] rounded-2xl p-3.5 sm:p-4 shadow-xs space-y-3.5">
      {/* 1. Header with Title, Actions & 120-Yr Tree Button */}
      <div className="flex items-center justify-between border-b border-astro-border pb-2.5 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-amber-500/10 border border-amber-300 text-amber-800 flex items-center justify-center font-bold text-xs">
            <Clock className="w-4 h-4 text-amber-700" />
          </div>
          <div>
            <h4 className="text-xs sm:text-sm font-bold text-[#263633] font-serif uppercase tracking-wider flex items-center gap-1.5">
              <span>{isHi ? 'सक्रिय विंशोत्तरी दशा प्रवाह' : 'Active Vimshottari Flow'}</span>
              <span className="text-[10px] font-sans font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.2 rounded-full">
                {isHi ? 'लाइव' : 'Live'}
              </span>
            </h4>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {/* Detailed Timing Slide-Over Drawer Button */}
          <button
            type="button"
            onClick={onOpenDashaDrawer}
            className="text-[11px] font-bold text-astro-primary hover:text-astro-secondary flex items-center gap-1 px-3 py-1.5 rounded-lg bg-astro-teal-tint hover:bg-astro-teal-tint/80 transition-colors cursor-pointer border border-astro-secondary/20 shadow-2xs"
            title={isHi ? 'विस्तृत ५-स्तरीय दशा एवं तात्कालिक समय चक्र' : 'View Detailed 5-Level Timing & Curated Horizon'}
          >
            <span>{isHi ? 'विस्तृत समय चक्र' : 'Detailed Timing'}</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* 2. Hero Triad Ribbon: MD -> AD -> PD (Responsive Grid / Stack) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 bg-white border border-astro-border/80 rounded-xl p-3 shadow-2xs">
        {/* Mahadasha (Macro) */}
        {activeTriad.md && (
          <div
            onClick={() => handleSpotlightPlanet(activeTriad.md!.planet)}
            className="p-2.5 rounded-lg border border-astro-border/70 bg-gradient-to-br from-[#FAF8F5] to-white hover:border-amber-400 hover:shadow-xs transition-all cursor-pointer space-y-1.5 group relative"
          >
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-astro-secondary uppercase tracking-widest flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-astro-secondary" />
                {isHi ? 'महादशा' : 'Mahadasha'}
              </span>
              <span className="text-[10px] font-mono font-semibold text-astro-secondary-text bg-[#FAF6ED] px-1.5 py-0.5 rounded border border-astro-border">
                {formatDashaDateYear(activeTriad.md.startDate)} – {formatDashaDateYear(activeTriad.md.endDate)}
              </span>
            </div>

            <div className="flex items-baseline justify-between">
              <div className="flex items-baseline gap-1.5">
                <span className="text-base font-bold text-astro-primary font-serif group-hover:text-amber-700 transition-colors">
                  {getPlanetShortCode(activeTriad.md.planet, activeLang)}
                </span>
                <span className="text-xs text-astro-secondary-text font-medium">
                  ({activeTriad.md.planet})
                </span>
                {activeTriad.mdMeta?.isRetrograde && (
                  <span className="text-[9px] font-extrabold text-amber-700 bg-amber-100 px-1 rounded" title="Retrograde (Vakri)">
                    R
                  </span>
                )}
                {activeTriad.mdMeta?.isCombust && (
                  <span className="text-[9px] font-extrabold text-rose-700 bg-rose-100 px-1 rounded" title="Combust (Asta)">
                    C
                  </span>
                )}
              </div>

              {activeTriad.mdMeta?.charaKarakaCode && (
                <span className="text-[9px] font-bold font-mono px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-900 border border-amber-300">
                  {activeTriad.mdMeta.charaKarakaCode}
                </span>
              )}
            </div>

            {/* Rulership & Placement */}
            <div className="text-[10.5px] text-astro-secondary-text font-medium flex items-center justify-between pt-0.5">
              <span>
                {activeTriad.mdMeta?.ruledHouses.length
                  ? isHi
                    ? `भावाधिपति: ${activeTriad.mdMeta.ruledHouses.join(', ')} भाव`
                    : `Lord of H${activeTriad.mdMeta.ruledHouses.join(', H')}`
                  : (isHi ? 'छाया ग्रह' : 'Nodes')}
                {isDivisional && activeTriad.mdMeta?.d1RuledHouses.length ? (
                  <span className="opacity-75 text-[9.5px]"> (D1: H{activeTriad.mdMeta.d1RuledHouses.join('/')})</span>
                ) : null}
              </span>
              {activeTriad.mdMeta?.placedHouse && (
                <span className="font-semibold text-astro-text">
                  in H{activeTriad.mdMeta.placedHouse}
                </span>
              )}
            </div>

            {/* Aspects (Drishti) */}
            {activeTriad.mdMeta?.aspects && activeTriad.mdMeta.aspects.length > 0 && (
              <div className="pt-1.5 border-t border-astro-border/60 flex items-center gap-1.5 flex-wrap text-[10px]">
                <span className="text-[9.5px] font-bold text-astro-secondary uppercase tracking-wider flex items-center gap-1 shrink-0">
                  <Eye className="w-3 h-3 text-astro-secondary" />
                  <span>{isHi ? 'दृष्टि:' : 'Aspects:'}</span>
                </span>
                <div className="flex items-center gap-1 flex-wrap">
                  {activeTriad.mdMeta.aspects.map((asp, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectHouse(asp.houseNumber);
                      }}
                      className="px-1.5 py-0.5 rounded bg-[#FAF6ED] hover:bg-amber-100 hover:border-amber-300 border border-astro-border text-astro-primary text-[9.5px] font-bold font-mono transition-colors cursor-pointer flex items-center gap-1 shadow-2xs"
                      title={
                        asp.occupyingPlanets.length > 0
                          ? `House ${asp.houseNumber} (${asp.occupyingPlanets.join(', ')})`
                          : `House ${asp.houseNumber} (Vacant)`
                      }
                    >
                      <span>H{asp.houseNumber}</span>
                      {asp.occupyingPlanets.length > 0 && (
                        <span className="text-[8.5px] text-amber-800 bg-amber-200/70 px-1 py-0.2 rounded font-sans">
                          {asp.occupyingPlanets.map(p => getPlanetShortCode(p, activeLang)).join(',')}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Antardasha (Active Highlight Phase) */}
        {activeTriad.ad && (
          <div
            onClick={() => handleSpotlightPlanet(activeTriad.ad!.planet)}
            className="p-2.5 rounded-lg border-2 border-amber-400 bg-amber-50/40 hover:bg-amber-50/70 hover:shadow-xs transition-all cursor-pointer space-y-1.5 group relative"
          >
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-extrabold text-amber-800 uppercase tracking-widest flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-amber-600 animate-pulse" />
                {isHi ? 'अंतर्दशा' : 'Antardasha'}
              </span>
              <span className="text-[10px] font-mono font-bold text-amber-900 bg-amber-100/90 px-1.5 py-0.5 rounded border border-amber-300">
                {activeTriad.adDaysRemaining} {isHi ? 'दिन शेष' : 'days left'}
              </span>
            </div>

            <div className="flex items-baseline justify-between">
              <div className="flex items-baseline gap-1.5">
                <span className="text-base font-extrabold text-amber-950 font-serif group-hover:text-amber-700 transition-colors">
                  {getPlanetShortCode(activeTriad.ad.planet, activeLang)}
                </span>
                <span className="text-xs text-amber-900/80 font-medium">
                  ({activeTriad.ad.planet})
                </span>
                {activeTriad.adMeta?.isRetrograde && (
                  <span className="text-[9px] font-extrabold text-amber-800 bg-amber-200 px-1 rounded" title="Retrograde (Vakri)">
                    R
                  </span>
                )}
                {activeTriad.adMeta?.isCombust && (
                  <span className="text-[9px] font-extrabold text-rose-700 bg-rose-100 px-1 rounded" title="Combust (Asta)">
                    C
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1">
                {activeTriad.adMeta?.charaKarakaCode && (
                  <span className="text-[9px] font-bold font-mono px-1.5 py-0.5 rounded bg-amber-200 text-amber-900">
                    {activeTriad.adMeta.charaKarakaCode}
                  </span>
                )}
                <span className="text-[9px] font-bold font-mono px-1.5 py-0.5 rounded bg-white text-amber-800 border border-amber-300">
                  {activeTriad.adProgressPercent}%
                </span>
              </div>
            </div>

            {/* Dates & Subtitle */}
            <div className="text-[10px] font-mono text-amber-900/80 truncate">
              {formatDashaMonthYear(activeTriad.ad.startDate)} – {formatDashaMonthYear(activeTriad.ad.endDate)}
            </div>

            {/* Mini Progress Bar */}
            <div className="w-full bg-amber-200/80 h-1.5 rounded-full overflow-hidden">
              <div
                className="bg-amber-600 h-full rounded-full transition-all duration-500"
                style={{ width: `${activeTriad.adProgressPercent}%` }}
              />
            </div>

            {/* Rulership & Placement */}
            <div className="text-[10px] text-amber-950 font-medium flex items-center justify-between">
              <span>
                {activeTriad.adMeta?.ruledHouses.length
                  ? `Lord of H${activeTriad.adMeta.ruledHouses.join('/')}`
                  : (isHi ? 'छाया ग्रह' : 'Nodes')}
              </span>
              {activeTriad.adMeta?.placedHouse && (
                <span className="font-bold">in H{activeTriad.adMeta.placedHouse}</span>
              )}
            </div>

            {/* Aspects (Drishti) */}
            {activeTriad.adMeta?.aspects && activeTriad.adMeta.aspects.length > 0 && (
              <div className="pt-1.5 border-t border-amber-300/80 flex items-center gap-1.5 flex-wrap text-[10px]">
                <span className="text-[9.5px] font-bold text-amber-900 uppercase tracking-wider flex items-center gap-1 shrink-0">
                  <Eye className="w-3 h-3 text-amber-700" />
                  <span>{isHi ? 'दृष्टि:' : 'Aspects:'}</span>
                </span>
                <div className="flex items-center gap-1 flex-wrap">
                  {activeTriad.adMeta.aspects.map((asp, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectHouse(asp.houseNumber);
                      }}
                      className="px-1.5 py-0.5 rounded bg-amber-100/90 hover:bg-amber-200 border border-amber-300 text-amber-950 text-[9.5px] font-bold font-mono transition-colors cursor-pointer flex items-center gap-1 shadow-2xs"
                      title={
                        asp.occupyingPlanets.length > 0
                          ? `House ${asp.houseNumber} (${asp.occupyingPlanets.join(', ')})`
                          : `House ${asp.houseNumber} (Vacant)`
                      }
                    >
                      <span>H{asp.houseNumber}</span>
                      {asp.occupyingPlanets.length > 0 && (
                        <span className="text-[8.5px] text-amber-900 bg-amber-200 px-1 py-0.2 rounded font-sans">
                          {asp.occupyingPlanets.map(p => getPlanetShortCode(p, activeLang)).join(',')}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pratyantardasha (Trigger Sub-Phase) */}
        {activeTriad.pd && (
          <div
            onClick={() => handleSpotlightPlanet(activeTriad.pd!.planet)}
            className="p-2.5 rounded-lg border border-astro-border/70 bg-gradient-to-br from-[#FAF8F5] to-white hover:border-emerald-400 hover:shadow-xs transition-all cursor-pointer space-y-1.5 group relative"
          >
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-astro-primary uppercase tracking-widest flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-emerald-600" />
                {isHi ? 'प्रत्यंतर्दशा' : 'Pratyantardasha'}
              </span>
              <span className="text-[10px] font-mono font-semibold text-emerald-800 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                {activeTriad.pd.daysRemaining} {isHi ? 'दिन शेष' : 'days left'}
              </span>
            </div>

            <div className="flex items-baseline justify-between">
              <div className="flex items-baseline gap-1.5">
                <span className="text-base font-bold text-emerald-900 font-serif group-hover:text-emerald-700 transition-colors">
                  {getPlanetShortCode(activeTriad.pd.planet, activeLang)}
                </span>
                <span className="text-xs text-astro-secondary-text font-medium">
                  ({activeTriad.pd.planet})
                </span>
                {activeTriad.pdMeta?.isRetrograde && (
                  <span className="text-[9px] font-extrabold text-amber-700 bg-amber-100 px-1 rounded" title="Retrograde">
                    R
                  </span>
                )}
                {activeTriad.pdMeta?.isCombust && (
                  <span className="text-[9px] font-extrabold text-rose-700 bg-rose-100 px-1 rounded" title="Combust">
                    C
                  </span>
                )}
              </div>

              {activeTriad.pdMeta?.charaKarakaCode && (
                <span className="text-[9px] font-bold font-mono px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-900 border border-emerald-200">
                  {activeTriad.pdMeta.charaKarakaCode}
                </span>
              )}
            </div>

            {/* Exact Date Range */}
            <div className="text-[10px] font-mono text-astro-secondary-text truncate">
              {formatDashaDayMonth(activeTriad.pd.startDate)} – {formatDashaFullDate(activeTriad.pd.endDate)}
            </div>

            {/* Rulership & Placement */}
            <div className="text-[10.5px] text-astro-secondary-text font-medium flex items-center justify-between pt-0.5">
              <span>
                {activeTriad.pdMeta?.ruledHouses.length
                  ? `Lord of H${activeTriad.pdMeta.ruledHouses.join('/')}`
                  : (isHi ? 'छाया ग्रह' : 'Nodes')}
              </span>
              {activeTriad.pdMeta?.placedHouse && (
                <span className="font-semibold text-astro-text">
                  in H{activeTriad.pdMeta.placedHouse}
                </span>
              )}
            </div>

            {/* Aspects (Drishti) */}
            {activeTriad.pdMeta?.aspects && activeTriad.pdMeta.aspects.length > 0 && (
              <div className="pt-1.5 border-t border-astro-border/60 flex items-center gap-1.5 flex-wrap text-[10px]">
                <span className="text-[9.5px] font-bold text-emerald-800 uppercase tracking-wider flex items-center gap-1 shrink-0">
                  <Eye className="w-3 h-3 text-emerald-700" />
                  <span>{isHi ? 'दृष्टि:' : 'Aspects:'}</span>
                </span>
                <div className="flex items-center gap-1 flex-wrap">
                  {activeTriad.pdMeta.aspects.map((asp, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectHouse(asp.houseNumber);
                      }}
                      className="px-1.5 py-0.5 rounded bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-300 border border-emerald-200 text-emerald-950 text-[9.5px] font-bold font-mono transition-colors cursor-pointer flex items-center gap-1 shadow-2xs"
                      title={
                        asp.occupyingPlanets.length > 0
                          ? `House ${asp.houseNumber} (${asp.occupyingPlanets.join(', ')})`
                          : `House ${asp.houseNumber} (Vacant)`
                      }
                    >
                      <span>H{asp.houseNumber}</span>
                      {asp.occupyingPlanets.length > 0 && (
                        <span className="text-[8.5px] text-emerald-800 bg-emerald-200/70 px-1 py-0.2 rounded font-sans">
                          {asp.occupyingPlanets.map(p => getPlanetShortCode(p, activeLang)).join(',')}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 2.5. Situational Saturn (Sade Sati & Dhaiyya) & Maraka Diagnostic Strip (Positioned directly beneath MD, AD, PD) */}
      {((shaniStatus && shaniStatus.isVisible) || dashaMarakaAlert) && (
        <div className={`grid gap-2 ${isNarrow ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'}`}>
          {/* Shani Status Pill */}
          {shaniStatus && shaniStatus.isVisible && (
            <div className={`p-2.5 rounded-xl border flex items-center justify-between gap-2 shadow-2xs ${
              shaniStatus.alertType === 'warning'
                ? 'bg-amber-50/80 border-amber-300 text-amber-900'
                : shaniStatus.alertType === 'info'
                ? 'bg-blue-50/80 border-blue-200 text-blue-900'
                : 'bg-[#FAF6ED]/70 border-astro-border text-astro-text'
            }`}>
              <div className="flex items-center gap-2 overflow-hidden">
                <span className="text-sm">🪐</span>
                <div className="truncate">
                  <span className="text-[11px] font-bold block truncate">
                    {shaniStatus.badge}: {shaniStatus.phase}
                  </span>
                  <span className="text-[10px] text-astro-secondary-text block truncate">
                    {shaniStatus.saturnSign} {shaniStatus.endDate ? `• ${shaniStatus.endDate}` : ''}
                  </span>
                </div>
              </div>
              {shaniStatus.alertType === 'warning' && (
                <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0 animate-pulse" />
              )}
            </div>
          )}

          {/* Markesh & Badhaka Diagnostic Pill */}
          {dashaMarakaAlert && (
            <div className={`p-2.5 rounded-xl border flex items-center justify-between gap-2 shadow-2xs ${
              dashaMarakaAlert.level === 'warning'
                ? 'bg-rose-50/70 border-rose-200 text-rose-900'
                : 'bg-[#FAF6ED]/70 border-astro-border text-astro-text'
            }`}
            title={`${dashaMarakaAlert.header}: ${dashaMarakaAlert.text}`}
            >
              <div className="flex items-center gap-2 overflow-hidden w-full">
                <ShieldAlert className={`w-4 h-4 shrink-0 ${dashaMarakaAlert.level === 'warning' ? 'text-rose-600' : 'text-astro-secondary'}`} />
                <div className="truncate flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-[11px] font-bold truncate">
                      {dashaMarakaAlert.header}
                    </span>
                    {marakaDiagnosis && (
                      <span className="text-[9.5px] font-mono text-astro-secondary-text shrink-0">
                        {marakaDiagnosis.lord2}, {marakaDiagnosis.lord7}
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] text-astro-secondary-text block truncate">
                    {dashaMarakaAlert.text}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 2.7. Stacked Live Gochar (Transit) & Double-Transit Synthesis Cockpit Strip */}
      <div className="bg-[#FAF8F5] border border-astro-border/80 rounded-xl p-3 space-y-2.5 shadow-2xs">
        <div className="flex items-center justify-between flex-wrap gap-1.5">
          <div className="flex items-center gap-1.5">
            <Radio className="w-3.5 h-3.5 text-rose-600 animate-pulse" />
            <span className="text-[11px] font-bold text-astro-primary font-serif uppercase tracking-wider">
              {isHi ? 'लाइव गोचर संचरण व द्वि-गोचर प्रभाव' : 'Live Gochar (Transits) & Double-Transit Trigger'}
            </span>
          </div>

          <div className="flex items-center gap-1.5 flex-wrap">
            {/* Double transit alert tag */}
            {gocharSnapshot.doubleTransitHouses.length > 0 && (
              <span className="text-[10px] font-bold text-indigo-900 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded-md flex items-center gap-1">
                <Zap className="w-3 h-3 text-indigo-600" />
                <span>{isHi ? 'गुरु-शनि द्वि-गोचर:' : 'Guru-Shani Axis:'}</span>
                <span className="font-mono">
                  H{gocharSnapshot.doubleTransitHouses.join(', H')}
                </span>
              </span>
            )}

            {gocharSnapshot.confluenceHouses.length > 0 && (
              <span className="text-[10px] font-bold text-amber-900 bg-amber-100/90 border border-amber-300 px-2 py-0.5 rounded-md flex items-center gap-1">
                <Activity className="w-3 h-3 text-amber-700" />
                <span>{isHi ? 'दशा-गोचर संगम:' : 'Dasha Confluence:'}</span>
                <span className="font-mono">H{gocharSnapshot.confluenceHouses.join(', H')}</span>
              </span>
            )}
          </div>
        </div>

        {/* Transiting Grahas Strip (Live Positions Relative to Lagna & Moon) */}
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-9 gap-1.5">
          {gocharSnapshot.transits.items.map((tp, idx) => {
            const isBenefic = tp.isBeneficTransitFromMoon;
            const isSaturnOrJupiter = tp.planet === 'Saturn' || tp.planet === 'Jupiter';
            const isActiveDashaPlanet =
              activeTriad.md?.planet === tp.planet ||
              activeTriad.ad?.planet === tp.planet ||
              activeTriad.pd?.planet === tp.planet;

            return (
              <div
                key={idx}
                onClick={() => onSelectHouse(tp.houseFromLagna)}
                className={`p-1.5 rounded-lg border text-left cursor-pointer transition-all hover:shadow-2xs ${
                  isActiveDashaPlanet
                    ? 'bg-amber-50/90 border-amber-300 ring-1 ring-amber-300/60'
                    : isSaturnOrJupiter
                    ? 'bg-white border-indigo-200 hover:border-indigo-300'
                    : 'bg-white border-astro-border/70 hover:border-astro-border'
                }`}
                title={`${tp.planet} in ${tp.currentSign} (${tp.formattedDegree})\nH${tp.houseFromLagna} from Lagna | H${tp.houseFromMoon} from Moon\n${tp.vedicEffect}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-astro-primary font-serif">
                    {getPlanetShortCode(tp.planet, activeLang)}
                  </span>
                  {tp.isRetrograde && (
                    <span className="text-[8px] font-bold text-amber-700 bg-amber-100 px-1 rounded">R</span>
                  )}
                  {isBenefic && (
                    <span className="text-[8px] font-bold text-emerald-700 bg-emerald-100 px-1 rounded">✓</span>
                  )}
                </div>

                <div className="text-[9.5px] font-medium text-astro-secondary-text truncate mt-0.5">
                  {getZodiacName(tp.currentSign, activeLang)}
                </div>

                <div className="text-[9px] font-mono text-astro-text font-bold flex items-center justify-between mt-0.5">
                  <span className="text-astro-primary">H{tp.houseFromLagna}</span>
                  <span className="text-astro-secondary-text text-[8.5px]">(M:{tp.houseFromMoon})</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 3. Tactical Horizon Mode Selector Tabs (Collapsed by Default) */}
      <div className="flex items-center justify-between border-t border-astro-border/60 pt-2.5 flex-wrap gap-2">
        <div className="inline-flex items-center bg-[#F4F1EA] p-0.5 rounded-xl border border-astro-border text-xs font-bold">
          <button
            type="button"
            onClick={() => setActiveTab(prev => prev === 'horizon' ? null : 'horizon')}
            className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
              activeTab === 'horizon'
                ? 'bg-white text-astro-primary shadow-2xs font-extrabold'
                : 'text-astro-secondary-text hover:text-astro-primary'
            }`}
          >
            <Calendar className="w-3.5 h-3.5 text-amber-600" />
            <span>{isHi ? 'आगामी २–५ वर्ष क्षितिज' : 'Upcoming 2–5 Years'}</span>
            {activeTab === 'horizon' ? (
              <ChevronUp className="w-3 h-3 text-astro-primary" />
            ) : (
              <ChevronDown className="w-3 h-3 text-astro-secondary-text" />
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab(prev => prev === 'pds' ? null : 'pds')}
            className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
              activeTab === 'pds'
                ? 'bg-white text-astro-primary shadow-2xs font-extrabold'
                : 'text-astro-secondary-text hover:text-astro-primary'
            }`}
          >
            <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
            <span>{isHi ? 'वर्तमान प्रत्यंतर (९ चरण)' : 'Current 9 PDs (Micro)'}</span>
            {activeTab === 'pds' ? (
              <ChevronUp className="w-3 h-3 text-astro-primary" />
            ) : (
              <ChevronDown className="w-3 h-3 text-astro-secondary-text" />
            )}
          </button>
        </div>

        <span className="text-[10px] text-astro-secondary-text hidden sm:inline">
          {activeTab === null
            ? (isHi ? 'विस्तार करने के लिए किसी भी विकल्प पर क्लिक करें' : 'Click either tab to expand detailed timeline')
            : (isHi ? 'संक्षिप्त करने के लिए पुनः क्लिक करें' : 'Click active tab again to collapse')}
        </span>
      </div>

      {/* 4. Tab Content: Current 9 Pratyantardashas (Micro-Timing) */}
      {activeTab === 'pds' && (
        <div className="space-y-2 animate-in fade-in duration-200">
          <div className="text-[11px] text-astro-secondary-text flex items-center justify-between">
            <span>
              {isHi
                ? `सक्रिय अंतर्दशा (${activeTriad.ad?.planet}) के ९ प्रत्यंतर चरण:`
                : `9 Pratyantardashas of active ${activeTriad.ad?.planet} Antardasha:`}
            </span>
            <span className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-md flex items-center gap-1">
              <Info className="w-3 h-3 text-amber-700" />
              <span>{isHi ? 'जन्म समय संवेदनशीलता: ±२ मिनट = ~८ दिन' : 'Birth Time: ±2 min shifts PD by ~8 days'}</span>
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {activeTriad.pds.map((pdItem, idx) => {
              const meta = getPlanetChartMeta(pdItem.planet);
              const isSelected = selectedPlanet?.name === pdItem.planet;

              return (
                <div
                  key={idx}
                  onClick={() => handleSpotlightPlanet(pdItem.planet)}
                  className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer relative ${
                    pdItem.isCurrent
                      ? 'bg-amber-50/80 border-amber-400 ring-1 ring-amber-300 shadow-2xs'
                      : isSelected
                      ? 'bg-emerald-50 border-emerald-400'
                      : 'bg-white hover:bg-[#FAF8F5] border-astro-border/80'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className="font-bold text-xs text-astro-primary font-serif">
                        {getPlanetShortCode(pdItem.planet, activeLang)}
                      </span>
                      <span className="text-[10.5px] text-astro-secondary-text">
                        ({pdItem.planet})
                      </span>
                      {meta.isRetrograde && (
                        <span className="text-[8.5px] font-extrabold text-amber-800 bg-amber-100 px-1 rounded">R</span>
                      )}
                    </div>

                    {pdItem.isCurrent ? (
                      <span className="text-[9px] font-bold text-amber-800 bg-amber-100 px-1.5 py-0.2 rounded-full animate-pulse">
                        ● {isHi ? 'सक्रिय' : 'Active'}
                      </span>
                    ) : (
                      <span className="text-[9px] font-mono text-astro-secondary-text">
                        {pdItem.durationMonths}m ({pdItem.durationDays}d)
                      </span>
                    )}
                  </div>

                  <div className="text-[10px] font-mono text-astro-secondary-text mt-1">
                    {formatDashaDayMonth(pdItem.startDate)} – {formatDashaFullDate(pdItem.endDate)}
                  </div>

                  <div className="text-[9.5px] text-astro-secondary-text mt-1 flex items-center justify-between">
                    <span>
                      {meta.ruledHouses.length ? `H${meta.ruledHouses.join('/')}` : 'Node'}
                      {meta.placedHouse ? ` in H${meta.placedHouse}` : ''}
                    </span>
                    {pdItem.isCurrent && (
                      <span className="font-bold font-mono text-amber-900">
                        {pdItem.daysRemaining}d left
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 5. Tab Content: Upcoming 2–5 Years Tactical Horizon (Multi-MD Seamless) */}
      {activeTab === 'horizon' && (
        <div className="space-y-2.5 animate-in fade-in duration-200">
          <div className="text-[11px] text-astro-secondary-text flex items-center justify-between">
            <span>
              {isHi
                ? 'आगामी २–५ वर्षों का कालानुक्रमिक विंशोत्तरी क्षितिज (महादशा संधिकाल सहित):'
                : 'Chronological 2–5 year horizon (with Mahadasha boundary transitions):'}
            </span>
            <span className="text-[10px] text-astro-secondary-text italic">
              {isHi ? 'विस्तृत प्रत्यंतर देखने के लिए कार्ड पर क्लिक करें' : 'Click any card to expand its 9 PDs inline'}
            </span>
          </div>

          <div className="space-y-2">
            {horizonPeriods.map((item, idx) => {
              const adMeta = getPlanetChartMeta(item.adPlanet);
              const isExpanded = expandedHorizonId === item.id;
              const isSelected = selectedPlanet?.name === item.adPlanet;

              return (
                <div key={item.id} className="space-y-1.5">
                  {/* Era Shift Banner if transitioning from prior MD */}
                  {item.isEraShift && (
                    <div className="p-2.5 rounded-xl bg-gradient-to-r from-purple-50 via-indigo-50 to-amber-50 border border-purple-200 flex items-center justify-between gap-2 shadow-2xs">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-700 shrink-0" />
                        <div>
                          <span className="text-xs font-bold text-purple-950 block">
                            {isHi ? '⚡ वृहद महादशा संधिकाल (Macro Era Shift)' : '⚡ Macro Era Shift: Mahadasha Transition'}
                          </span>
                          <span className="text-[10px] text-purple-800">
                            {item.prevMdPlanet} MD {isHi ? 'समाप्त' : 'concludes'} ➔ {item.mdPlanet} MD {isHi ? 'प्रारंभ' : 'begins'} ({formatDashaMonthYear(item.startDate)})
                            {item.ageStart ? ` • ${isHi ? `आयु: ${item.ageStart} वर्ष` : `Age: ${item.ageStart} yrs`}` : ''}
                          </span>
                        </div>
                      </div>
                      <span className="text-[9.5px] font-bold px-2 py-0.5 rounded-full bg-purple-200/80 text-purple-900 shrink-0">
                        {isHi ? 'जीवन परिवर्तन' : 'New Chapter'}
                      </span>
                    </div>
                  )}

                  {/* Antardasha Card */}
                  <div
                    className={`rounded-xl border transition-all ${
                      item.isCurrent
                        ? 'bg-amber-50/60 border-amber-300 ring-1 ring-amber-300/80 shadow-2xs'
                        : isSelected
                        ? 'bg-emerald-50/70 border-emerald-300'
                        : 'bg-white hover:bg-[#FAF8F5] border-astro-border/80'
                    }`}
                  >
                    <div
                      onClick={() => setExpandedHorizonId(isExpanded ? null : item.id)}
                      className="p-3 flex items-center justify-between cursor-pointer select-none flex-wrap gap-2"
                    >
                      {/* Left: Planets & Status */}
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <div className="flex items-center gap-1.5">
                          {isExpanded ? (
                            <ChevronDown className="w-3.5 h-3.5 text-astro-secondary" />
                          ) : (
                            <ChevronRight className="w-3.5 h-3.5 text-astro-secondary-text" />
                          )}
                          <span className="text-xs font-bold text-astro-secondary-text font-mono">
                            {item.mdPlanet} —
                          </span>
                          <span className="text-sm font-bold text-astro-primary font-serif">
                            {getPlanetShortCode(item.adPlanet, activeLang)}
                          </span>
                          <span className="text-xs text-astro-secondary-text font-medium">
                            ({item.adPlanet})
                          </span>
                          {adMeta.isRetrograde && (
                            <span className="text-[9px] font-extrabold text-amber-800 bg-amber-100 px-1 rounded">R</span>
                          )}
                        </div>

                        {/* Status Badges */}
                        {item.isCurrent ? (
                          <span className="text-[9px] font-bold text-amber-800 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-full">
                            ● {isHi ? 'सक्रिय' : 'Active'} ({item.daysRemaining}d left)
                          </span>
                        ) : (
                          <span className="text-[10px] text-astro-secondary-text font-mono bg-[#FAF6ED] px-2 py-0.5 rounded-md border border-astro-border">
                            {item.durationYears}y
                          </span>
                        )}

                        {/* Dasha Chidra Warning */}
                        {item.isDashaChidra && (
                          <span className="text-[9.5px] font-bold text-rose-800 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded-md flex items-center gap-1">
                            <AlertTriangle className="w-2.5 h-2.5" />
                            <span>{isHi ? 'दशा छिद्र' : 'Dasha Chidra'}</span>
                          </span>
                        )}

                        {/* MD-AD Relationship Badge */}
                        <span
                          className={`text-[9.5px] font-semibold px-2 py-0.5 rounded-md border ${
                            item.relationshipWithMd.nature === 'harmonious'
                              ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                              : item.relationshipWithMd.nature === 'frictional'
                              ? 'bg-rose-50 text-rose-800 border-rose-200'
                              : item.relationshipWithMd.nature === 'intense'
                              ? 'bg-orange-50 text-orange-800 border-orange-200'
                              : 'bg-astro-bg text-astro-secondary-text border-astro-border'
                          }`}
                        >
                          {isHi ? item.relationshipWithMd.labelHi : item.relationshipWithMd.labelEn}
                        </span>
                      </div>

                      {/* Right: Dates, Age & House Activation */}
                      <div className="flex items-center gap-3 shrink-0 text-right">
                        <div>
                          <div className="text-xs font-mono font-bold text-astro-primary">
                            {formatDashaMonthYear(item.startDate)} – {formatDashaMonthYear(item.endDate)}
                          </div>
                          <div className="text-[10px] text-astro-secondary-text flex items-center justify-end gap-1.5">
                            {item.ageStart && item.ageEnd && (
                              <span className="font-semibold text-astro-secondary">
                                {isHi ? `आयु: ${item.ageStart}–${item.ageEnd} वर्ष` : `Age: ${item.ageStart}–${item.ageEnd} yrs`}
                              </span>
                            )}
                            <span>•</span>
                            <span>
                              {adMeta.ruledHouses.length ? `H${adMeta.ruledHouses.join('/')}` : 'Node'}
                              {adMeta.placedHouse ? ` in H${adMeta.placedHouse}` : ''}
                            </span>
                          </div>
                        </div>

                        {/* Spotlight Button */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSpotlightPlanet(item.adPlanet);
                          }}
                          className="px-2 py-1 rounded bg-[#FAF6ED] hover:bg-amber-100 text-astro-primary hover:text-amber-900 border border-astro-border text-[10px] font-bold transition-colors cursor-pointer"
                          title="Spotlight in House Inspector"
                        >
                          {isHi ? 'भाव देखें' : 'View House'}
                        </button>
                      </div>
                    </div>

                    {/* Inline Accordion for 9 Pratyantardashas */}
                    {isExpanded && (
                      <div className="p-3 bg-[#FAF8F5] border-t border-astro-border space-y-2 animate-in fade-in duration-150">
                        <div className="text-[10.5px] font-bold text-astro-secondary flex items-center justify-between">
                          <span>
                            {item.mdPlanet} — {item.adPlanet} {isHi ? 'के ९ प्रत्यंतर चरण:' : '9 Sub-Phases (PDs):'}
                          </span>
                          <span className="text-[9.5px] text-astro-secondary-text font-mono">
                            {formatDashaFullDate(item.startDate)} → {formatDashaFullDate(item.endDate)}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          {item.pratyantardashas.map((pdSub, subIdx) => {
                            const subMeta = getPlanetChartMeta(pdSub.planet);
                            return (
                              <div
                                key={subIdx}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSpotlightPlanet(pdSub.planet);
                                }}
                                className={`p-2 rounded-lg border text-left cursor-pointer transition-colors ${
                                  pdSub.isCurrent
                                    ? 'bg-amber-100/70 border-amber-400 font-bold'
                                    : 'bg-white hover:bg-astro-bg border-astro-border/70'
                                }`}
                              >
                                <div className="flex items-center justify-between text-[11px]">
                                  <span className="font-bold text-astro-primary">
                                    {getPlanetShortCode(pdSub.planet, activeLang)} ({pdSub.planet})
                                  </span>
                                  <span className="text-[9px] font-mono text-astro-secondary-text">
                                    {pdSub.durationMonths}m
                                  </span>
                                </div>
                                <div className="text-[9px] font-mono text-astro-secondary-text mt-0.5">
                                  {formatDashaDayMonth(pdSub.startDate)} – {formatDashaDayMonth(pdSub.endDate)}
                                </div>
                                <div className="text-[8.5px] text-astro-secondary-text mt-0.5">
                                  {subMeta.ruledHouses.length ? `H${subMeta.ruledHouses.join('/')}` : 'Node'}
                                  {subMeta.placedHouse ? ` in H${subMeta.placedHouse}` : ''}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
